import { CompanyModel } from 'api/model';
import SearchableSelect, {
  SearchableSelectItem,
  SelectOption,
} from 'components/inputs/SearchableSelect';
import useCompanies from 'contexts/basicData/useCompanies';
import { FC, useMemo } from 'react';
import styled, { css, FlattenSimpleInterpolation } from 'styled-components';
import { DropdownPosition } from './Dropdown';

export const StyledCompanySelectItem = styled(SearchableSelectItem)<{
  isNoneItem?: boolean;
}>`
  ${({ isNoneItem }) =>
    isNoneItem &&
    css`
      font-style: italic;
    `}
`;

function optionRender(
  key: string,
  option: SelectOption<number | null, CompanyModel | null>,
  onClick: () => void,
  isSelected: boolean
) {
  return (
    <StyledCompanySelectItem
      isNoneItem={option.value === null}
      key={key}
      onClick={onClick}
      selected={isSelected}
    >
      {option.label}
    </StyledCompanySelectItem>
  );
}

interface Props {
  selectedCompanyId: number | null;
  value?: number | null;
  onChange?(value: number | null): void;
  onCompanySelected(company: CompanyModel | null): void;
  filter?(company: CompanyModel): boolean;
  hideInput?: boolean;
  notNullable?: boolean;
  nullValueLabel?: string;
  dropdownPosition?: DropdownPosition;
  itemsWrapperStyle?: FlattenSimpleInterpolation;
  disabled?: boolean;
  renderItem?: (
    key: string,
    option: SelectOption<number | null, CompanyModel | null>,
    onClick: () => void,
    isSelected: boolean
  ) => React.ReactNode;
  /**
   * Optional options that should always be shown at the top of the dropdown.
   * Useful for showing case-specific addresses (from/to) as quick picks.
   */
  topOptions?: SelectOption<number | null, CompanyModel | null>[];
}

const CompanyPicker: FC<Props> = ({
  selectedCompanyId,
  onCompanySelected,
  value,
  onChange,
  filter,
  hideInput,
  disabled,
  itemsWrapperStyle,
  notNullable,
  nullValueLabel,
  dropdownPosition,
  renderItem,
  topOptions,
}) => {
  const { companies, allCompanies } = useCompanies();

  const selectedCompany = value || selectedCompanyId;

  const handleOptionClicked = (
    _companyId: number | null,
    _selected: boolean,
    option: SelectOption<number | null, CompanyModel | null>
  ) => {
    onCompanySelected(option.extra);
    onChange?.(option.value);
  };

  const options = useMemo(() => {
    // base options from companies
    const companyOptions: SelectOption<number | null, CompanyModel | null>[] =
      companies
        .filter((company) => (filter ? filter(company) : true))
        .map((company) => ({
          label: company.name,
          value: company.companyID,
          extra: company,
        }));

    // start with optional topOptions (if provided) so they appear first
    const theOptions: SelectOption<number | null, CompanyModel | null>[] = (
      topOptions && topOptions.length ? [...topOptions] : []
    ).concat(companyOptions);

    if (!notNullable) {
      theOptions.unshift({
        label: nullValueLabel ?? 'Inget',
        value: null,
        extra: null,
      });
    }

    // Ensure selected company is present even if it's no longer selectable
    const selectedCompanyNoLongerASelectableOption = !theOptions.find(
      (opt) => opt.value === selectedCompany
    );
    const selectedNowInactiveCompany = allCompanies.find(
      (company) => company.companyID === selectedCompany && !company.active
    );

    if (
      selectedCompanyNoLongerASelectableOption &&
      selectedNowInactiveCompany
    ) {
      theOptions.unshift({
        label: selectedNowInactiveCompany.name,
        value: selectedCompany,
        extra: selectedNowInactiveCompany,
      });
    }

    // Remove duplicate options by value (keep first occurrence, which prefers topOptions)
    const seen = new Set<any>();
    const deduped: SelectOption<number | null, CompanyModel | null>[] = [];
    for (const opt of theOptions) {
      const key = opt.value ?? opt.label;
      if (!seen.has(key)) {
        seen.add(key);
        deduped.push(opt);
      }
    }

    return deduped;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allCompanies,
    companies,
    filter,
    notNullable,
    nullValueLabel,
    selectedCompany,
    topOptions,
  ]);

  return (
    <SearchableSelect
      dropdownPosition={dropdownPosition}
      hideInput={hideInput}
      disabled={disabled || companies.length === 0}
      itemsWrapperStyle={itemsWrapperStyle}
      onOptionClicked={handleOptionClicked}
      optionRender={renderItem || optionRender}
      options={options}
      searchFilter={(searchString, opts) => {
        const lowerSearchStr = searchString.toLowerCase();
        return opts.filter((opt) => {
          // Always include items that come from topOptions (they were prepended)
          const isTop = topOptions?.some(
            (t: SelectOption<number | null, CompanyModel | null>) =>
              t.value === opt.value || t.label === opt.label
          );
          if (isTop) return true;

          // Only show active companies, the 'Inget' option, and all company names that include the search string
          return (
            opt.value === null ||
            (opt.extra?.active &&
              opt.extra?.name.toLowerCase().includes(lowerSearchStr))
          );
        });
      }}
      selectedValue={selectedCompany}
    />
  );
};

export default CompanyPicker;
